import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Brain, Cpu, Layers, Globe, Server, Database, Rocket, Shield, Activity } from "lucide-react";
import { NuvraLogo } from "@/components/NuvraLogo";
import { useI18n } from "@/i18n/I18nContext";

// ─── Design constants ───────────────────────────────────────────────────────
const DW = 600;
const DH = 460;
const CX = 300;
const CY = 230;

// ─── Node definitions ───────────────────────────────────────────────────────
function polar(cx: number, cy: number, r: number, deg: number) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

interface NodeDef {
  id: string;
  label: string;
  Icon: React.ElementType;
  hue: number;
  x: number;
  y: number;
  delay: number;
}

const innerNodes: NodeDef[] = [
  { id: "ai",      label: "AI Engine", Icon: Brain,  hue: 200, delay: 0.3, ...polar(CX, CY, 125, 270) },
  { id: "codegen", label: "Code Gen",  Icon: Cpu,    hue: 265, delay: 0.4, ...polar(CX, CY, 125, 30)  },
  { id: "arch",    label: "Architect", Icon: Layers, hue: 215, delay: 0.5, ...polar(CX, CY, 125, 150) },
];

const outerNodes: NodeDef[] = [
  { id: "frontend", label: "Frontend",    Icon: Globe,     hue: 150, delay: 0.6, ...polar(CX, CY, 225, 270) },
  { id: "api",      label: "API Gateway", Icon: Server,    hue: 45,  delay: 0.7, ...polar(CX, CY, 225, 330) },
  { id: "db",       label: "Database",    Icon: Database,  hue: 30,  delay: 0.8, ...polar(CX, CY, 225, 30)  },
  { id: "deploy",   label: "Deploy",      Icon: Rocket,    hue: 315, delay: 0.9, ...polar(CX, CY, 225, 90)  },
  { id: "auth",     label: "Auth",        Icon: Shield,    hue: 0,   delay: 1.0, ...polar(CX, CY, 225, 150) },
  { id: "monitor",  label: "Monitor",     Icon: Activity,  hue: 175, delay: 1.1, ...polar(CX, CY, 225, 210) },
];

const allNodes = [...innerNodes, ...outerNodes];

// ─── Node label translation keys ────────────────────────────────────────────
const NODE_LABEL_KEYS: Record<string, string> = {
  ai:       "of.nodeAi",
  codegen:  "of.nodeCodegen",
  arch:     "of.nodeArch",
  frontend: "of.nodeFrontend",
  api:      "of.nodeApi",
  db:       "of.nodeDb",
  deploy:   "of.nodeDeploy",
  auth:     "of.nodeAuth",
  monitor:  "of.nodeMonitor",
};

// ─── Edge definitions ───────────────────────────────────────────────────────
interface EdgeDef {
  fromId: string;
  toId: string;
  hue: number;
}

const NUCLEUS_NODE: NodeDef = { id: "nucleus", label: "", Icon: NuvraLogo, hue: 255, x: CX, y: CY, delay: 0.15 };

const edges: EdgeDef[] = [
  { fromId: "nucleus", toId: "ai",       hue: 200 },
  { fromId: "nucleus", toId: "codegen",  hue: 265 },
  { fromId: "nucleus", toId: "arch",     hue: 215 },
  { fromId: "ai",      toId: "frontend", hue: 150 },
  { fromId: "ai",      toId: "api",      hue: 45  },
  { fromId: "codegen", toId: "db",       hue: 30  },
  { fromId: "codegen", toId: "auth",     hue: 0   },
  { fromId: "arch",    toId: "deploy",   hue: 315 },
  { fromId: "arch",    toId: "monitor",  hue: 175 },
];

// ─── Packet state - initialized once, lives outside component ───────────────
interface Packet {
  edgeIndex: number;
  progress: number;
  speed: number;
}

const packetState: Packet[] = [];
let packetsInitialized = false;

function ensurePackets() {
  if (packetsInitialized) return;
  packetsInitialized = true;
  edges.forEach((_, i) => {
    packetState.push({ edgeIndex: i, progress: 0,   speed: 0.05 + Math.random() * 0.05 });
    packetState.push({ edgeIndex: i, progress: 0.5, speed: 0.05 + Math.random() * 0.05 });
  });
}

/*
 * There was a log ticker here, cycling invented lines: "Schema resolved: users (6 fields)",
 * "Running health checks: 3/3 pass", "Deploying to production edge". None of it described
 * anything that had happened. It was fabricated system output on a public page, which is the
 * exact shape of claim Nuvra refuses to make about a customer's software, and it made a
 * considered architecture diagram read as a developer demo with a fake terminal bolted on.
 *
 * Removed rather than rewritten. The field already carries real motion (packet flow along the
 * edges, node pulses, the nucleus glow), so the space it occupied lets the composition breathe
 * instead of needing something to fill it.
 */

// ─── Main component ──────────────────────────────────────────────────────────
export function OrchestrationField() {
  const { t } = useI18n();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // scale: maps DW to available width, capped at 1.5x (matches maxWidth 900 on desktop)
  const [scale, setScale] = useState(1);

  // Measure available width and compute uniform scale so the fixed DW x DH
  // design space shrinks/grows proportionally on every viewport size.
  useEffect(() => {
    function measure() {
      if (!wrapperRef.current) return;
      const availW = wrapperRef.current.clientWidth;
      setScale(Math.min(1.5, availW / DW));
    }
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapperRef.current) ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, []);

  // Build a lookup: id to node
  const nodeMap = new Map<string, NodeDef>();
  nodeMap.set("nucleus", NUCLEUS_NODE);
  allNodes.forEach((n) => nodeMap.set(n.id, n));

  useEffect(() => {
    ensurePackets();

    const canvas = canvasRef.current;
    if (!canvas) return;

    let ctx: CanvasRenderingContext2D | null = null;

    function getCtx() {
      if (!canvas) return null;
      if (!ctx) ctx = canvas.getContext("2d");
      return ctx;
    }

    function resizeCanvas() {
      if (!canvas) return;
      const c = getCtx();
      if (!c) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        c.scale(dpr, dpr);
      }
    }

    function drawFrame(timestamp: number) {
      if (!canvas) return;
      const c = getCtx();
      if (!c) return;

      resizeCanvas();

      const dt = lastTimeRef.current ? (timestamp - lastTimeRef.current) / 1000 : 0.016;
      lastTimeRef.current = timestamp;

      // Canvas is always DW x DH CSS pixels (inner design div, not scaled).
      // Use uniform 1:1 mapping so edges and packets are never distorted.
      function toCanvas(dx: number, dy: number): [number, number] {
        return [dx, dy];
      }

      c.clearRect(0, 0, DW, DH);

      // Nucleus ambient glow
      const [ncx, ncy] = toCanvas(CX, CY);
      const glowR = 60;
      const nucleusGlow = c.createRadialGradient(ncx, ncy, 0, ncx, ncy, glowR);
      nucleusGlow.addColorStop(0, "hsla(255, 60%, 70%, 0.12)");
      nucleusGlow.addColorStop(1, "hsla(255, 60%, 50%, 0)");
      c.save();
      c.fillStyle = nucleusGlow;
      c.beginPath();
      c.arc(ncx, ncy, glowR, 0, Math.PI * 2);
      c.fill();
      c.restore();

      // Draw edges
      edges.forEach((edge) => {
        const fromNode = nodeMap.get(edge.fromId);
        const toNode = nodeMap.get(edge.toId);
        if (!fromNode || !toNode) return;

        const [x1, y1] = toCanvas(fromNode.x, fromNode.y);
        const [x2, y2] = toCanvas(toNode.x, toNode.y);

        c.save();
        c.globalAlpha = 0.18;
        c.strokeStyle = `hsl(${edge.hue}, 70%, 60%)`;
        c.lineWidth = 1;
        c.beginPath();
        c.moveTo(x1, y1);
        c.lineTo(x2, y2);
        c.stroke();
        c.restore();
      });

      // Update and draw packets
      packetState.forEach((pkt) => {
        pkt.progress += pkt.speed * dt;
        if (pkt.progress > 1) pkt.progress -= 1;

        const edge = edges[pkt.edgeIndex];
        const fromNode = nodeMap.get(edge.fromId);
        const toNode = nodeMap.get(edge.toId);
        if (!fromNode || !toNode) return;

        const [x1, y1] = toCanvas(fromNode.x, fromNode.y);
        const [x2, y2] = toCanvas(toNode.x, toNode.y);

        const px = x1 + (x2 - x1) * pkt.progress;
        const py = y1 + (y2 - y1) * pkt.progress;

        // Glow halo
        const haloR = 7;
        const halo = c.createRadialGradient(px, py, 0, px, py, haloR);
        halo.addColorStop(0, `hsla(${edge.hue}, 80%, 75%, 0.5)`);
        halo.addColorStop(1, `hsla(${edge.hue}, 80%, 65%, 0)`);
        c.save();
        c.fillStyle = halo;
        c.beginPath();
        c.arc(px, py, haloR, 0, Math.PI * 2);
        c.fill();
        c.restore();

        // Core dot
        c.save();
        c.globalAlpha = 0.95;
        c.fillStyle = "white";
        c.beginPath();
        c.arc(px, py, 2, 0, Math.PI * 2);
        c.fill();
        c.restore();
      });

      animRef.current = requestAnimationFrame(drawFrame);
    }

    animRef.current = requestAnimationFrame(drawFrame);

    return () => {
      cancelAnimationFrame(animRef.current);
    };
  // nodeMap is rebuilt each render but that's fine - the effect only runs once
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Outer wrapper reserves exact scaled height so the page flow is correct.
  // Inner div stays at design size (DW x DH) and is visually scaled by CSS transform.
  // This ensures every element - canvas, icons, labels - shrinks/grows uniformly.
  const scaledH = Math.round(DH * scale);

  return (
    <div
      ref={wrapperRef}
      className="relative mx-auto overflow-hidden"
      style={{ width: "100%", maxWidth: 900, height: scaledH }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: DW,
          height: DH,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
      {/* Canvas layer - edges + packets */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ width: DW, height: DH, willChange: "transform" }}
      />

      {/* Nucleus */}
      <motion.div
        className="absolute"
        style={{
          left: `${(CX / DW) * 100}%`,
          top: `${(CY / DH) * 100}%`,
          transform: "translate(-50%, -50%)",
        }}
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: NUCLEUS_NODE.delay, ease: "easeOut" }}
      >
        <div className="relative flex flex-col items-center gap-1">
          {/* Pulse rings */}
          <div className="relative">
            <motion.div
              className="absolute inset-0 rounded-full border border-primary/25"
              animate={{ scale: [1, 1.8, 1], opacity: [0.6, 0, 0.6] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="absolute inset-0 rounded-full border border-primary/15"
              animate={{ scale: [1, 2.4, 1], opacity: [0.4, 0, 0.4] }}
              transition={{ duration: 3.8, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
            />
            <div
              className="relative w-10 h-10 rounded-full border-2 border-primary/50 bg-background shadow-lg flex items-center justify-center"
              style={{ boxShadow: "0 0 20px hsl(255 60% 60% / 0.2)" }}
            >
              <NuvraLogo size={22} />
            </div>
          </div>
          <span
            className="uppercase tracking-widest text-primary/60"
            style={{ fontSize: 8 }}
          >
            {t("of.nucleus")}
          </span>
        </div>
      </motion.div>

      {/* All nodes */}
      {allNodes.map((node) => {
        const leftPct = (node.x / DW) * 100;
        const topPct = (node.y / DH) * 100;
        const Icon = node.Icon;

        return (
          <motion.div
            key={node.id}
            className="absolute"
            style={{
              left: `${leftPct}%`,
              top: `${topPct}%`,
              transform: "translate(-50%, -40%)",
            }}
            initial={{ opacity: 0, scale: 0.3 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: node.delay, ease: "easeOut" }}
            whileHover={{ scale: 1.1 }}
          >
            <div className="flex flex-col items-center gap-1">
              <div
                className="relative w-10 h-10 rounded-lg flex items-center justify-center"
                style={{
                  background: `hsl(${node.hue}, 70%, 60%, 0.10)`,
                  border: `1px solid hsl(${node.hue}, 70%, 60%, 0.30)`,
                  boxShadow: `0 0 12px hsl(${node.hue}, 70%, 60%, 0.15)`,
                }}
              >
                {/* Status dot */}
                <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_4px_hsl(160_70%_50%/0.7)]" />
                <Icon
                  className="h-[18px] w-[18px]"
                  style={{ color: `hsl(${node.hue}, 70%, 65%)` }}
                />
              </div>
              <span
                className="font-semibold text-foreground/80 whitespace-nowrap"
                style={{ fontSize: 9 }}
              >
                {t(NODE_LABEL_KEYS[node.id] ?? node.id)}
              </span>
            </div>
          </motion.div>
        );
      })}

      </div> {/* end inner design-size div */}
    </div>
  );
}
